/**
 * Locale-aware Dutch (Belgian) formatters and small derivations the SPA
 * needs to bridge the API's wire format (ISO dates, decimal strings) and
 * what the design handoff shows ("over 4 dagen", "€11 240", "2u geleden").
 */

import type { IconName } from '@/design-system/icon/Icon';

const TODAY = () => new Date();

export function formatCurrencyEUR(value: number | string): string {
  const n = typeof value === 'string' ? Number(value) : value;
  return new Intl.NumberFormat('nl-BE', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(Number.isFinite(n) ? n : 0);
}

export function formatNumberNlBE(value: number | string): string {
  const n = typeof value === 'string' ? Number(value) : value;
  return new Intl.NumberFormat('nl-BE', { maximumFractionDigits: 0 }).format(
    Number.isFinite(n) ? n : 0,
  );
}

export function toNumber(value: number | string | null | undefined): number {
  if (value === null || value === undefined) return 0;
  const n = typeof value === 'string' ? Number(value) : value;
  return Number.isFinite(n) ? n : 0;
}

/**
 * "12/06/2025" — short Dutch (dd/mm/yyyy) from an ISO date or DateOnly.
 */
export function formatShortDate(iso: string | Date): string {
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()}`;
}

/**
 * "13/05" — even shorter, used in deadlines and weekly summaries.
 */
export function formatDayMonth(iso: string | Date): string {
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}`;
}

/**
 * "over 4 dagen" / "vandaag" / "2 dagen te laat" — relative deadline phrase.
 */
export function formatRelativeDeadline(iso: string | Date): string {
  const target = typeof iso === 'string' ? new Date(iso) : iso;
  if (Number.isNaN(target.getTime())) return '';
  const ms = target.getTime() - startOfDay(TODAY()).getTime();
  const days = Math.round(ms / 86_400_000);
  if (days === 0) return 'vandaag';
  if (days === 1) return 'morgen';
  if (days === -1) return 'gisteren';
  if (days > 0) return `over ${days} dagen`;
  return `${Math.abs(days)} dagen te laat`;
}

/**
 * "2u geleden" / "gisteren" / "5 dagen geleden" — relative past phrase.
 */
export function formatRelativePast(iso: string | Date): string {
  const past = typeof iso === 'string' ? new Date(iso) : iso;
  if (Number.isNaN(past.getTime())) return '';
  const diffMs = TODAY().getTime() - past.getTime();
  if (diffMs < 0) return '';
  const minutes = Math.round(diffMs / 60_000);
  if (minutes < 60) return `${Math.max(1, minutes)} min geleden`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}u geleden`;
  const days = Math.round(hours / 24);
  if (days === 1) return 'gisteren';
  if (days < 7) return `${days}d geleden`;
  const weeks = Math.round(days / 7);
  if (weeks < 5) return `${weeks}w geleden`;
  const months = Math.round(days / 30);
  return `${months}m geleden`;
}

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

/**
 * Stable colour hue for avatars based on a name, so the same person keeps the
 * same colour across screens without storing a hue in the database.
 */
export function hueForName(name: string): number {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = (hash * 31 + name.charCodeAt(i)) | 0;
  }
  return ((hash % 360) + 360) % 360;
}

/**
 * Map activity verb to its design-system icon. Unknown verbs fall back to
 * the generic bell so the activity feed never breaks layout.
 */
export function iconForVerb(verb: string): IconName {
  if (verb.startsWith('mail')) return 'mail';
  if (verb.startsWith('photo')) return 'image';
  if (verb.startsWith('invoice')) return 'receipt';
  if (verb.startsWith('document')) return 'paperclip';
  if (verb.startsWith('task')) return 'check';
  if (verb.startsWith('decision')) return 'decision';
  return 'bell';
}

/**
 * Map activity verb to Dutch past-tense phrase. Mirrors the handoff's tone
 * ("stuurde een mail", "uploaden") without burdening the API with copy.
 */
export function dutchVerb(verb: string): string {
  switch (verb) {
    case 'mail.sent':
      return 'stuurde een mail';
    case 'mail.received':
      return 'ontving een mail';
    case 'photo.added':
      return "foto's toegevoegd";
    case 'invoice.received':
      return 'factuur ontvangen';
    case 'document.uploaded':
      return 'document geüpload';
    case 'task.done':
      return 'taak afgerond';
    case 'decision.taken':
      return 'beslissing genomen';
    default:
      return verb;
  }
}

/**
 * Try to parse JSON payload from the activity entry; returns undefined on
 * any error so callers can fall back to the verb's default copy.
 */
export function parsePayload<T = Record<string, unknown>>(json: string | null): T | undefined {
  if (json === null || json === undefined) return undefined;
  try {
    return JSON.parse(json) as T;
  } catch {
    return undefined;
  }
}
